import api from "src/apis/api"

// ── Scope helpers ─────────────────────────────────────────────────────────────
// A document/folder request is either top-level (the whole-org library) or
// nested under a parent record. `scope` is { type, id } where type is one of
// "lead" | "deal" | "booking" | "project", or null/undefined for top-level.
const PARENT_PATHS = {
  lead: "leads",
  deal: "deals",
  booking: "bookings",
  project: "projects",
}

function basePath(scope) {
  if (scope?.type && scope?.id) {
    const segment = PARENT_PATHS[scope.type]
    if (!segment) throw new Error(`Unknown document scope: ${scope.type}`)
    return `/api/v1/${segment}/${scope.id}`
  }
  return "/api/v1"
}

// ── Documents ────────────────────────────────────────────────────────────────
export async function fetchDocuments(scope, filters = {}) {
  const { data } = await api.get(`${basePath(scope)}/documents`, { params: filters })
  return data
}

export async function uploadDocument(scope, file, metadata = {}) {
  const form = new FormData()
  form.append("document[file]", file)
  Object.entries(metadata).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      form.append(`document[${key}]`, value)
    }
  })
  const { data } = await api.post(`${basePath(scope)}/documents`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  })
  return data.document
}

export async function updateDocument(documentId, metadata) {
  const payload = {}
  Object.entries(metadata).forEach(([key, value]) => {
    payload[key] = value
  })
  const { data } = await api.patch(`/api/v1/documents/${documentId}`, { document: payload })
  return data.document
}

export async function deleteDocument(documentId) {
  const { data } = await api.delete(`/api/v1/documents/${documentId}`)
  return data
}

// ── Folders ──────────────────────────────────────────────────────────────────
export async function fetchFolders(scope, parentFolderId = null) {
  const params = parentFolderId ? { parent_folder_id: parentFolderId } : {}
  const { data } = await api.get(`${basePath(scope)}/document_folders`, { params })
  return data.document_folders || []
}

export async function createFolder(scope, name, parentFolderId = null) {
  const { data } = await api.post(`${basePath(scope)}/document_folders`, {
    document_folder: { name, parent_folder_id: parentFolderId },
  })
  return data.document_folder
}

export async function deleteFolder(folderId) {
  const { data } = await api.delete(`/api/v1/document_folders/${folderId}`)
  return data
}

// ── Compliance ─────────────────────────────────────────────────────────────
export async function fetchExpiringDocuments() {
  const { data } = await api.get("/api/v1/documents/expiring")
  return data.documents || []
}

export async function fetchBookingChecklist(bookingId) {
  const { data } = await api.get(`/api/v1/bookings/${bookingId}/document_checklist`)
  return data.checklist
}

// ── Sharing ──────────────────────────────────────────────────────────────────
export async function shareDocument(documentId, { recipients, message }) {
  const { data } = await api.post(`/api/v1/documents/${documentId}/share`, { recipients, message })
  return data.shares || []
}

export async function fetchDocumentShares(documentId) {
  const { data } = await api.get(`/api/v1/documents/${documentId}/shares`)
  return data.shares || []
}
